// src/fees/xlsx.js — Excel експорт на „Такси и плащания".
//
// ЗАЩО отделен файл (същата причина като print.js): екселът е ДРУГ документ, не
// екранът. Тук се решава само кое става колона и в какъв ред — екранът остава
// чист от заглавия на листове, а промяна по единия не пипа другия.
//
// ВАЖНО 1 (числа): в клетките влизат ЧИСЛА в ЕВРО, не текст с „€". Иначе Excel
// ги приема за думи и собственикът не може да сумира колона — точно заради това
// се прави експортът. Преобразуването „центове → число" е ЕДНО за целия файл
// (eurNum): две биха се разминали някой ден, както учи normalizeId на сървъра.
//
// ВАЖНО 2 (един лист): общият помощник xlsxDownload слага ЕДИН лист във файл, а
// src/shared/xlsx.js е общ за целия проект и не се пипа заради този екран.
// Затова всяка справка се сваля като ОТДЕЛЕН файл, а екранът го КАЗВА, за да не
// изглежда, че останалите справки са се загубили.
//
// ВАЖНО 3 (поверителност): същото правило като печата — в листа влиза само това,
// което е колона на екрана. Бележките по клиента, ЕИК и имейли остават вътре.
//
// ВАЖНО 4 (кое се изнася): моделът идва ГОТОВ от src/fees/index.js — вече
// пресят през филтрите и през избора на видими колони. Така файлът повтаря
// екрана, вместо да е втора, различна справка.

import { xlsxDownload } from '../shared/xlsx.js';
import { toast } from '../shared/toast.js';

// Центове → число за Excel. ЕДНОТО преобразуване на този файл.
// Стотинката е цяло число, затова първо се закръгля тя, а после се дели —
// иначе едно случайно дробно центче би внесло опашка от знаци след запетаята.
export function eurNum(cents) {
  return Math.round(Number(cents) || 0) / 100;
}

// Празна справка не бива да сваля файл с нула реда: човек го отваря, вижда само
// заглавия и решава, че системата е загубила данните. Казваме защо е празно.
function download(rows, sheet, file, emptyMsg) {
  if (!rows || !rows.length) { toast(emptyMsg, true); return false; }
  xlsxDownload(rows, sheet, file);
  return true;
}

// ---- Матрицата (клиент × месеци) -------------------------------------------
//
// model: { year, debtHead, months: [{ month, label }], cols: {...},
//          rows: [{ label, signature, accountant, fee_base_cents,
//                   fee_active_cents, carryover_cents, months: [центове],
//                   adjustments_cents, debt_cents }] }
//
// Колоните се решават от model.cols — СЪЩИЯТ избор като на екрана и на печата.
// Заглавието на дълга идва наготово („Дълг към Юли"), защото месецът, докъдето
// е начислено, го знае само екранът.
export function feesMatrixRows(model) {
  const cols = (model && model.cols) || {};
  const months = (model && model.months) || [];
  const debtHead = (model && model.debtHead) || 'Дълг';
  return ((model && model.rows) || []).map((r) => {
    const o = {};
    o['Клиент'] = r.label || '';
    if (cols.signature) o['Подпис'] = r.signature || '';
    if (cols.accountant) o['Счетоводител'] = r.accountant || '';
    if (cols.fees) {
      o['Такса D'] = eurNum(r.fee_base_cents);
      o['Такса E'] = eurNum(r.fee_active_cents);
    }
    if (cols.carry) o['Пренос'] = eurNum(r.carryover_cents);
    months.forEach((m, i) => { o[m.label] = eurNum((r.months || [])[i]); });
    if (cols.adj) o['Корекции'] = eurNum(r.adjustments_cents);
    o[debtHead] = eurNum(r.debt_cents);
    return o;
  });
}

export function exportFeesMatrix(model) {
  const year = (model && model.year) || '';
  return download(feesMatrixRows(model), 'Такси ' + year, 'taksi-' + year + '.xlsx',
    'Няма какво да се свали — таблицата е празна по текущия филтър.');
}

// ---- Разходи ---------------------------------------------------------------
//
// model: { year, rows: [{ month (име), category (надпис), person, amount_cents, note }] }
// Месецът идва като ИМЕ и категорията като НАДПИС — и двете живеят в екрана,
// за да не се появи втори списък с имена, който да се разминава с първия.
export function expenseRows(model) {
  return ((model && model.rows) || []).map((r) => ({
    'Месец': r.month || '',
    'Категория': r.category || '',
    'Човек': r.person || '',
    'Сума': eurNum(r.amount_cents),
    'Бележка': r.note || '',
  }));
}

export function exportExpenses(model) {
  const year = (model && model.year) || '';
  return download(expenseRows(model), 'Разходи ' + year, 'razhodi-' + year + '.xlsx',
    'Още няма записани разходи за ' + year + ' г.');
}

// ---- Печалба по месеци -----------------------------------------------------
//
// model: { year, rows: [{ month (име), income_cents, expense_cents, profit_cents }] }
// Загубата излиза като ОТРИЦАТЕЛНО число, не като „▼ −1 830,00 €": знакът на
// екрана е за окото, а Excel трябва да може да събира колоната.
export function profitRows(model) {
  return ((model && model.rows) || []).map((r) => ({
    'Месец': r.month || '',
    'Приход': eurNum(r.income_cents),
    'Разход': eurNum(r.expense_cents),
    'Печалба': eurNum(r.profit_cents),
  }));
}

export function exportProfit(model) {
  const year = (model && model.year) || '';
  return download(profitRows(model), 'Печалба ' + year, 'pechalba-' + year + '.xlsx',
    'Няма данни за печалбата за ' + year + ' г.');
}

// ---- Приход по счетоводител и месец ----------------------------------------
//
// model: { year, mode: 'paid' | 'expected', monthLabels: [12 имена],
//          rows: [{ name, months: [центове], total_cents }] }
// Изнася се това, което е ИЗБРАНО на екрана (платено или начислено) — и то пише
// в името на файла и на листа, иначе двата файла се различават само по числата.
export function accountantMonthsRows(model) {
  const labels = (model && model.monthLabels) || [];
  return ((model && model.rows) || []).map((r) => {
    const o = { 'Счетоводител': r.name || '' };
    labels.forEach((label, i) => { o[label] = eurNum((r.months || [])[i]); });
    o['Общо'] = eurNum(r.total_cents);
    return o;
  });
}

export function exportAccountantMonths(model) {
  const year = (model && model.year) || '';
  const paid = !model || model.mode !== 'expected';
  const word = paid ? 'платено' : 'начислено';
  return download(accountantMonthsRows(model),
    'Счетоводители ' + year, 'prihod-schetovoditeli-' + word + '-' + year + '.xlsx',
    'Няма данни по счетоводители за ' + year + ' г.');
}

// ---- Табло „Счетоводители" -------------------------------------------------
//
// model: { year, period ('до Юли 2026'), rows: [{ name, clients, expected_cents,
//          direct_cost_cents, shared_cost_cents, cost_cents, profit_cents, note }] }
//
// ВАЖНО („Платено" го няма): колоната отпадна от ЕКРАНА по искане на
// собственика, значи отпада и тук — файлът повтаря екрана, а не е втора,
// различна справка. „Остава" е начислено минус общ разход (смята се в екрана).
//
// ВАЖНО (периодът): „Период" е ПЪРВА колона на всеки ред, не само в името на
// файла. Листът тръгва по хората сам и „Разход 2 100" без месец лъже точно
// толкова, колкото и на екрана — а името на файла се губи при копиране.
//
// ВАЖНО (липсващо ≠ нула): числото, което сървърът не дава, излиза като ПРАЗНА
// клетка, не като 0. Нулата тук би значела „не му начисляваме нищо" и
// собственикът би я събрал в сбора, без да разбере.
function orBlank(v) { return v == null ? '' : eurNum(v); }

export function accountantsBoardRows(model) {
  const period = (model && model.period) || '';
  return ((model && model.rows) || []).map((r) => ({
    'Период': period,
    'Счетоводител': r.name || '',
    'Клиенти': r.clients == null ? '' : Number(r.clients) || 0,
    'Начислено': orBlank(r.expected_cents),
    'Пряк разход': orBlank(r.direct_cost_cents),
    'Споделен разход': orBlank(r.shared_cost_cents),
    'Разход общо': orBlank(r.cost_cents),
    'Остава': orBlank(r.profit_cents),
    'Бележка': r.note || '',
  }));
}

export function exportAccountantsBoard(model) {
  const year = (model && model.year) || '';
  return download(accountantsBoardRows(model),
    'Табло ' + year, 'tablo-schetovoditeli-' + year + '.xlsx',
    'Няма данни по счетоводители за ' + year + ' г.');
}

// ---- Разходи по хора -------------------------------------------------------
//
// model: { year, month (1..12 или null), period, shared (има ли такава колона),
//          rows: [{ person, salary_cents, insurance_cents, other_cents,
//                   shared_cents, amount_cents }] }
//
// „Споделени" е делът от общите разходи (наем, софтуер), които не са на нечие
// име. ВАЖНО: „Общо" е ПРЯКО + СПОДЕЛЕНО, тоест сборът на колоната е целият
// разход на кантората — заради това изобщо се появи колоната.
//
// ВАЖНО (кога я няма): стар сървър не дели общите разходи и не праща
// shared_cents. Тогава колоната отпада ЦЯЛАТА (model.shared е false), както и на
// екрана: колона с празни клетки се чете като „на този човек не се пада нищо
// общо", а истината е, че сървърът още не смята това число.
//
// Месецът влиза в името на файла като ЧИСЛО (m03), не като българско име:
// името на файла минава през файловата система на човека и там кирилицата е
// по-рискована от една цифра.
export function expensesByPersonRows(model) {
  const period = (model && model.period) || '';
  const shared = !!(model && model.shared);
  return ((model && model.rows) || []).map((r) => {
    const o = {
      'Период': period,
      'Човек': r.person || '',
      'Заплати': orBlank(r.salary_cents),
      'Осигуровки': orBlank(r.insurance_cents),
      'Други': orBlank(r.other_cents),
    };
    // Редът на ключовете е редът на колоните в листа — затова „Споделени" се
    // слага ТУК, между „Други" и „Общо", точно както е на екрана.
    if (shared) o['Споделени'] = orBlank(r.shared_cents);
    o['Общо'] = eurNum(r.amount_cents);
    return o;
  });
}

export function exportExpensesByPerson(model) {
  const year = (model && model.year) || '';
  const m = Number(model && model.month) || 0;
  const tail = m ? '-m' + String(m).padStart(2, '0') : '';
  return download(expensesByPersonRows(model),
    'Разходи по хора ' + year, 'razhodi-po-hora-' + year + tail + '.xlsx',
    'Още няма разходи на нечие име за ' + year + ' г.');
}
